import { sub } from "date-fns";

const updateGroupClassEventsCallback = ({
  groupClassEventExternalId,
  scheduleData,
}: {
  groupClassEventExternalId: string;
  scheduleData: GroupedCombinedScheduleDataListType[];
}): GroupedCombinedScheduleDataListType[] => {
  return scheduleData
    .map((day) => ({
      ...day,
      data: day.data.filter((schedule) => {
        if (!schedule.isGroupClassEvent) {
          return true;
        }
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
        const groupClassEvent = schedule as GroupClassEventResponse;
        return (
          groupClassEvent.groupClassEventExternalId !==
          groupClassEventExternalId
        );
      }),
    }))
    .filter((day) => day.data.length > 0);
};

const getScheduleEventsOptions = ({
  appointmentTypes,
  isGroupClass,
  isPastEvents,
  isPatient,
  pageNumber,
  pageSize,
  patientExternalId,
}: {
  appointmentTypes?: string[];
  isGroupClass: boolean;
  isPastEvents: boolean;
  isPatient: boolean;
  pageNumber: string;
  pageSize: string;
  patientExternalId?: string;
}) => {
  const params = buildURLBaseParams({
    appointmentTypes,
    isGroupClass,
    isPatient,
    pageNumber,
    pageSize,
    patientExternalId,
  });
  const filterDate = isPastEvents
    ? sub(new Date(), { days: 7 }).toISOString()
    : sub(new Date(), { hours: 1 }).toISOString();

  const filterKey = isPastEvents ? "end-date" : "start-date";
  params.append("filter-by", `${filterKey}::${filterDate}&status::scheduled`);

  if (isPastEvents && !isGroupClass) {
    params.append("sort-order", "DESC");
  }

  return {
    params,
    url: isGroupClass
      ? "/schedule/family/group-class-events"
      : "/schedule/family/appointment-details",
  };
};

const buildURLBaseParams = ({
  appointmentTypes,
  isGroupClass,
  isPatient,
  pageNumber,
  pageSize,
  patientExternalId,
}: {
  appointmentTypes?: string[];
  isGroupClass: boolean;
  isPatient: boolean;
  pageNumber: string;
  pageSize: string;
  patientExternalId?: string;
}) => {
  const params = new URLSearchParams();
  if (pageSize) params.append("page-size", pageSize);
  if (pageNumber) params.append("page-number", pageNumber);
  if (patientExternalId && !isPatient) {
    params.append(
      isGroupClass ? "patient-external-id" : "patient-id",
      patientExternalId,
    );
  }

  if (appointmentTypes)
    params.append("appointment-types", appointmentTypes.join(","));

  return params;
};

const getGroupClassEventsOptions = (options: ScheduleEventsQueryParams) =>
  getScheduleEventsOptions({
    isGroupClass: true,
    isPastEvents: options.isPastEvents,
    isPatient: options.isPatient || false,
    pageNumber: options.pageNumber.toString(),
    pageSize: options.pageSize.toString(),
    patientExternalId: options.patientExternalId,
  });

// v2 group section events: like v1, send only an open-ended `start-date` (no end date).
// The endpoint requires a start date and rejects a start↔end range wider than 7 days, but
// that cap only applies when an end date is present — omitting it yields all upcoming
// events (bounded by page-size), matching v1. patient-external-id is only sent for support
// users viewing a linked patient.
const getGroupSectionEventsOptions = (options: ScheduleEventsQueryParams) => {
  const params = new URLSearchParams();
  params.append("page-size", options.pageSize.toString());
  params.append("page-number", options.pageNumber.toString());
  if (options.patientExternalId && !options.isPatient) {
    params.append("patient-external-id", options.patientExternalId);
  }
  const startDate = sub(new Date(), { hours: 1 }).toISOString();
  params.append("filter-by", buildFilterByParams({ startDate }));

  return { params, url: "/schedule/family/v2/group-section-events" };
};

const getAppointmentsOptions = (options: ScheduleEventsQueryParams) =>
  getScheduleEventsOptions({
    appointmentTypes: options.appointmentTypes,
    isGroupClass: false,
    isPastEvents: options.isPastEvents,
    isPatient: options.isPatient || false,
    pageNumber: options.pageNumber.toString(),
    pageSize: options.pageSize.toString(),
    patientExternalId: options.patientExternalId,
  });

const buildFilterByParams = ({
  endDate,
  startDate,
}: {
  endDate?: string;
  startDate?: string;
}) => {
  let filterByParams = "";
  if (startDate && endDate) {
    filterByParams = `start-date::${startDate}&end-date::${endDate}&status::scheduled`;
  } else if (startDate && !endDate) {
    filterByParams = `start-date::${startDate}&status::scheduled`;
  } else if (!startDate && endDate) {
    filterByParams = `end-date::${endDate}&status::scheduled`;
  } else {
    filterByParams = "status::scheduled";
  }
  return filterByParams;
};

export {
  buildFilterByParams,
  getAppointmentsOptions,
  getGroupClassEventsOptions,
  getGroupSectionEventsOptions,
  updateGroupClassEventsCallback,
};
